/**
 * AppSidebar - Navegação principal lateral da aplicação
 */
import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@context/AuthContext'
import ChangePasswordModal from '@components/shared/ChangePasswordModal'
import ThemeToggle from '@components/shared/ThemeToggle'
import PjeQuickCopyButton from '@components/pje/PjeQuickCopyButton'
import { INTERNAL_BRAND } from '@config/brand'

const matchesPath = (currentPath, targetPath) => (
  currentPath === targetPath || currentPath.startsWith(`${targetPath}/`)
)

const isNavItemActive = (currentPath, item) => (
  item.children?.some((child) => matchesPath(currentPath, child.path))
  || matchesPath(currentPath, item.path)
)

export default function AppHeader({ currentPath, onNavigate, sidebarOpen, sidebarCollapsed, onToggleSidebar, onCloseSidebar }) {
  const { user, logout, changePassword, canAccessBoard, canAccessDashboard, canAccessReports, canAccessProcessReports, canAccessClickBusReports, canAccessUsers, canAccessSimulator, canManageSimulatorSettings, canManageLegalTemplates, canAccessForms, canAccessSystemSettings } = useAuth()
  const [loggingOut, setLoggingOut] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [passwordModalOpen, setPasswordModalOpen] = useState(false)
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [passwordError, setPasswordError] = useState('')
  const userMenuRef = useRef(null)
  const isFirstLogin = Boolean(user?.forcePasswordChange)

  useEffect(() => {
    if (isFirstLogin) {
      setPasswordModalOpen(true)
      setUserMenuOpen(false)
    }
  }, [isFirstLogin])

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [])

  const navGroups = [
    {
      id: 'controladoria',
      index: '01',
      label: 'Controlador',
      accent: 'IA',
      items: [
        canAccessBoard ? { path: '/board', label: 'Kanban', icon: 'fa-columns', description: 'Gestão diária dos prazos' } : null,
        canAccessBoard ? { path: '/pauta-audiencias', label: 'Pauta de audiências', icon: 'fa-calendar-days', description: 'Audiências por advogado, estado, área e processo' } : null,
      ].filter(Boolean),
    },
    {
      id: 'eficiencia',
      index: '02',
      label: 'Eficienc',
      accent: 'IA',
      items: [
        canAccessDashboard ? { path: '/dashboard', label: 'Dashboard', icon: 'fa-chart-line', description: 'Indicadores e performance' } : null,
      ].filter(Boolean),
    },
    {
      id: 'adjutorem',
      index: '03',
      label: 'Adjutorem',
      accent: 'IA',
      separatedAccent: true,
      items: [
        canAccessReports && (canAccessProcessReports || canAccessClickBusReports)
          ? {
              path: '/relatorios',
              label: 'Relatórios',
              icon: 'fa-chart-pie',
              description: 'Análises por carteira',
              children: [
                canAccessProcessReports ? { path: '/relatorios/processos', label: 'Processos por cliente', icon: 'fa-chart-pie' } : null,
                canAccessClickBusReports ? { path: '/relatorios/clickbus', label: 'ClickBus', icon: 'fa-bus' } : null,
              ].filter(Boolean),
            }
          : null,
      ].filter(Boolean),
    },
    {
      id: 'panaceia',
      index: '04',
      label: 'Panace',
      accent: 'IA',
      items: [
        canAccessSystemSettings ? { path: '/publicacoes-monitoramento', label: 'Publicações', icon: 'fa-newspaper', description: 'Monitoramento de diário' } : null,
        canAccessSimulator ? { path: '/deadline-counter', label: 'Contador', icon: 'fa-calendar-check', description: 'Prazos por UF e tribunal' } : null,
        canAccessSimulator ? { path: '/deadline-simulator', label: 'Simulador', icon: 'fa-stopwatch', description: 'Teste do DeadlineEngine' } : null,
        canManageSimulatorSettings ? { path: '/calendario-juridico', label: 'Calendário jurídico', icon: 'fa-calendar-alt', description: 'Dias não úteis e sincronizações' } : null,
        canManageLegalTemplates ? { path: '/modelos-pecas', label: 'Modelos de peças', icon: 'fa-file-lines', description: 'Editor visual de documentos' } : null,
        canManageLegalTemplates || canManageSimulatorSettings ? { path: '/base-juridica/configuracoes', label: 'Base jurídica', icon: 'fa-book-open', description: 'Configuração das fontes da IA' } : null,
        canManageSimulatorSettings ? { path: '/deadline-engine/configuracoes', label: 'Config. Motor', icon: 'fa-cogs', description: 'Admin DeadlineEngine' } : null,
      ].filter(Boolean),
    },
    {
      id: 'administrativo',
      index: '05',
      label: 'Administrativo',
      items: [
        canAccessUsers ? { path: '/usuarios', label: 'Usuários', icon: 'fa-users-cog', description: 'Acessos e permissões' } : null,
        canAccessForms ? { path: '/formulario-submissoes', label: 'Fichas RH', icon: 'fa-clipboard-list', description: 'Análise das submissões públicas' } : null,
      ].filter(Boolean),
    },
  ].filter((group) => group.items.length > 0)

  const systemUtility = canAccessSystemSettings
    ? { path: '/configuracoes-sistema', label: 'Config. Sistema', icon: 'fa-gears', description: 'Infraestrutura e agenda técnica' }
    : null

  const handleLogout = async () => {
    const proceed = () => {
      setLoggingOut(true)
      setUserMenuOpen(false)
      setTimeout(() => {
        logout()
      }, 500)
    }
    const beforeLogout = new CustomEvent('auth:before-logout', {
      cancelable: true,
      detail: { proceed },
    })
    if (window.dispatchEvent(beforeLogout)) proceed()
  }

  const handleNavigate = (path) => {
    onNavigate(path)
    onCloseSidebar?.()
  }

  const handlePasswordSubmit = async (payload) => {
    setPasswordLoading(true)
    setPasswordError('')

    try {
      const result = await changePassword(payload)
      if (!result.success) {
        setPasswordError(result.error || 'Não foi possível alterar a senha.')
        return result
      }

      setPasswordModalOpen(false)
      setUserMenuOpen(false)
      return result
    } finally {
      setPasswordLoading(false)
    }
  }

  return (
    <>
      {sidebarOpen ? <button type="button" className="app-sidebar-backdrop" onClick={onCloseSidebar} aria-label="Fechar navegação" /> : null}

      <aside className={`app-sidebar ${sidebarOpen ? 'is-open' : ''} ${sidebarCollapsed ? 'is-collapsed' : ''}`}>
        <div className="app-sidebar-brand">
          <div className="app-sidebar-logo" aria-hidden="true">
            <img src={INTERNAL_BRAND.symbolPath} alt="" />
          </div>
          <div className="app-sidebar-brand-copy">
            <strong>{INTERNAL_BRAND.stem}<em>{INTERNAL_BRAND.accent}</em></strong>
            <span>{INTERNAL_BRAND.tagline}</span>
          </div>
          <button
            type="button"
            className="app-sidebar-collapse"
            onClick={onToggleSidebar}
            aria-label={sidebarCollapsed ? "Expandir menu" : "Reduzir menu"}
          >
            <i className={`fas ${sidebarCollapsed ? "fa-angles-right" : "fa-angles-left"}`} />
          </button>
          <button type="button" className="app-sidebar-close" onClick={onCloseSidebar} aria-label="Fechar menu">
            <i className="fas fa-times" />
          </button>
        </div>

        <nav className="app-sidebar-nav" aria-label="Navegação principal">
          {navGroups.map((group) => {
            const groupActive = group.items.some((item) => isNavItemActive(currentPath, item))
            const groupName = `${group.label}${group.separatedAccent ? ' ' : ''}${group.accent || ''}`

            return (
              <section className={`app-sidebar-group ${groupActive ? 'is-active' : ''}`} aria-label={groupName} key={group.id}>
                <h2 className="app-sidebar-group-heading">
                  <span className="app-sidebar-group-index" aria-hidden="true">{group.index}</span>
                  <strong>
                    {group.label}{group.separatedAccent ? ' ' : ''}
                    {group.accent ? <em>{group.accent}</em> : null}
                  </strong>
                  <span className="app-sidebar-group-rule" aria-hidden="true" />
                </h2>

                <div className="app-sidebar-group-items">
                  {group.items.map((item) => {
                    const isActive = isNavItemActive(currentPath, item)

                    return (
                      <div className={`app-sidebar-item ${item.children ? 'has-children' : ''}`} key={item.path}>
                        <button
                          type="button"
                          className={`app-sidebar-link ${isActive ? 'active' : ''}`}
                          onClick={() => handleNavigate(item.children?.[0]?.path || item.path)}
                          aria-label={item.label}
                          aria-current={!item.children && isActive ? 'page' : undefined}
                          aria-expanded={item.children ? isActive : undefined}
                          title={sidebarCollapsed ? item.label : undefined}
                        >
                          <i className={`fas ${item.icon}`} />
                          <span>
                            <strong>{item.label}</strong>
                            <small>{item.description}</small>
                          </span>
                        </button>
                        {item.children && isActive ? (
                          <div className="app-sidebar-subnav" aria-label={`Subopções de ${item.label}`}>
                            {item.children.map((child) => (
                              <button
                                type="button"
                                key={child.path}
                                className={`app-sidebar-sublink ${matchesPath(currentPath, child.path) ? 'active' : ''}`}
                                onClick={() => handleNavigate(child.path)}
                                aria-label={child.label}
                                aria-current={matchesPath(currentPath, child.path) ? 'page' : undefined}
                              >
                                <i className={`fas ${child.icon}`} />
                                <span>{child.label}</span>
                              </button>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    )
                  })}
                </div>
              </section>
            )
          })}

          {systemUtility ? (
            <div className="app-sidebar-utility" role="group" aria-label="Infraestrutura">
              <button
                type="button"
                className={`app-sidebar-link app-sidebar-utility-link ${matchesPath(currentPath, systemUtility.path) ? 'active' : ''}`}
                onClick={() => handleNavigate(systemUtility.path)}
                aria-label={systemUtility.label}
                aria-current={matchesPath(currentPath, systemUtility.path) ? 'page' : undefined}
                title={sidebarCollapsed ? systemUtility.label : undefined}
              >
                <i className={`fas ${systemUtility.icon}`} />
                <span>
                  <strong>{systemUtility.label}</strong>
                  <small>{systemUtility.description}</small>
                </span>
              </button>
            </div>
          ) : null}
        </nav>

        <div className="app-sidebar-footer" ref={userMenuRef}>
          <PjeQuickCopyButton />
          <ThemeToggle />
          <button
            type="button"
            className={`user-menu-trigger ${userMenuOpen ? 'is-open' : ''}`}
            onClick={() => setUserMenuOpen((prev) => !prev)}
            aria-haspopup="menu"
            aria-expanded={userMenuOpen}
          >
            <div className="user-info">
              <i className="fas fa-user-circle" />
              <span>
                <strong>{user?.nome || 'Usuário'}</strong>
                <small>{user?.role || 'N/A'}</small>
              </span>
            </div>
            <i className={`fas fa-chevron-${userMenuOpen ? 'up' : 'down'}`} />
          </button>

          {userMenuOpen ? (
            <div className="user-dropdown" role="menu">
              {isFirstLogin ? <p className="user-dropdown-alert">Primeiro acesso detectado. Defina uma nova senha para continuar.</p> : null}
              <button
                type="button"
                className="user-dropdown-item"
                onClick={() => {
                  setPasswordError('')
                  setPasswordModalOpen(true)
                  setUserMenuOpen(false)
                }}
              >
                <i className="fas fa-key" />
                <span>Alterar senha</span>
              </button>
              <button type="button" className="user-dropdown-item danger" onClick={handleLogout} disabled={loggingOut}>
                <i className={`fas ${loggingOut ? 'fa-spinner fa-spin' : 'fa-sign-out-alt'}`} />
                <span>{loggingOut ? 'Saindo...' : 'Realizar logout'}</span>
              </button>
            </div>
          ) : null}
        </div>
      </aside>

      <ChangePasswordModal
        isOpen={passwordModalOpen}
        onClose={() => {
          if (!isFirstLogin) {
            setPasswordModalOpen(false)
            setPasswordError('')
          }
        }}
        onSubmit={handlePasswordSubmit}
        loading={passwordLoading}
        error={passwordError}
        forced={isFirstLogin}
        requireCurrentPassword={!isFirstLogin}
      />
    </>
  )
}
